import React from 'react';
import { MapPin, CheckCircle2, Clock } from 'lucide-react';
import { coverageData, images } from '../data/mock';
import { PageSEO } from '../components/PageSEO';

const Coverage = () => {
  return (
    <div className="min-h-screen pt-20">
      <PageSEO path="/cobertura" />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">
              Cobertura Geográfica
            </h1>
            <p className="text-xl sm:text-2xl text-teal-100 leading-relaxed">
              Presencia estratégica en la zona centro del Ecuador con planes de expansión
            </p>
          </div>
        </div>
      </section>

      {/* Coverage Map Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Map Image */}
            <div className="relative order-2 lg:order-1">
              <img
                src={images.coverage}
                alt="Cobertura en Ecuador"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -top-6 -right-6 w-48 h-48 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl -z-10"></div>
            </div>

            {/* Coverage Info */}
            <div className="order-1 lg:order-2">
              <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-6">
                Nuestra Cobertura
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                Zona Centro del Ecuador
              </h2>

              {/* Current Coverage */}
              <div className="mb-10">
                <div className="flex items-center mb-4">
                  <CheckCircle2 className="text-emerald-600 mr-2" size={24} />
                  <h3 className="text-2xl font-bold text-gray-900">Cobertura Actual</h3>
                </div>
                <div className="space-y-3">
                  {coverageData.currentProvinces.map((province, index) => (
                    <div
                      key={index}
                      className="flex items-center p-4 bg-emerald-50 rounded-lg border-l-4 border-emerald-600 hover:bg-emerald-100 transition-colors"
                    >
                      <MapPin className="text-emerald-600 mr-3" size={20} />
                      <span className="text-lg font-semibold text-gray-800">{province.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Projected Coverage */}
              {coverageData.projectedProvinces.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <Clock className="text-teal-600 mr-2" size={24} />
                  <h3 className="text-2xl font-bold text-gray-900">En Proyección</h3>
                </div>
                <div className="space-y-3">
                  {coverageData.projectedProvinces.map((province, index) => (
                    <div
                      key={index}
                      className="flex items-center p-4 bg-teal-50 rounded-lg border-l-4 border-teal-600 hover:bg-teal-100 transition-colors"
                    >
                      <MapPin className="text-teal-600 mr-3" size={20} />
                      <span className="text-lg font-semibold text-gray-800">{province.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestra Presencia en Números
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Datos que reflejan nuestro compromiso con la cobertura efectiva
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full mb-4">
                <MapPin size={32} className="text-white" />
              </div>
              <h3 className="text-5xl font-bold text-emerald-600 mb-2">7</h3>
              <p className="text-xl font-semibold text-gray-800 mb-2">Provincias Activas</p>
              <p className="text-gray-600">Con operaciones en pleno funcionamiento</p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-full mb-4">
                <Clock size={32} className="text-white" />
              </div>
              <h3 className="text-5xl font-bold text-teal-600 mb-2">+6K</h3>
              <p className="text-xl font-semibold text-gray-800 mb-2">Clientes Activos</p>
              <p className="text-gray-600">En toda nuestra zona de cobertura</p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full mb-4">
                <CheckCircle2 size={32} className="text-white" />
              </div>
              <h3 className="text-5xl font-bold text-emerald-600 mb-2">100%</h3>
              <p className="text-xl font-semibold text-gray-800 mb-2">Cobertura Regional</p>
              <p className="text-gray-600">En zonas activas del centro del país</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mountain Image Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src={images.mountains}
              alt="Región centro del Ecuador"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent flex items-end">
              <div className="p-8 text-white">
                <h3 className="text-3xl font-bold mb-2">Centro del Ecuador</h3>
                <p className="text-xl text-gray-300">
                  Presencia estratégica en una de las regiones más importantes del país
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            ¿Buscas Expandir tu Marca?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
            Nuestra cobertura estratégica en el centro del Ecuador puede ayudarte a alcanzar nuevos mercados
          </p>
          <a
            href="/contacto"
            className="inline-block px-8 py-4 bg-white text-emerald-700 font-semibold rounded-lg hover:bg-emerald-50 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Conversemos Sobre tu Expansión
          </a>
        </div>
      </section>
    </div>
  );
};

export default Coverage;
